/**
 * Store to track global state to the client
 * @module
 */
var _ = require('optimizely-client-fn');
var actionTypes = require('../constants/action_types');
var freeze = require('../utils/freeze');

module.exports = {
  initialize: function() {
    this.__state = {
      holdback: 0,
      isGlobalHoldback: null,
      listTargetingKeys: [],
      revision: null,
      projectId: null,
      accountId: null,
      namespace: null,
      activationId: null,
      activationTimestamp: null,
      dcpServiceId: null,
      dcpKeyfieldLocators: [],
      recommenderServices: [],
      anonymizeIP: null,
      projectJS: null,
      snippetId: null,
      plugins: [],
      domContentLoaded: false,
      experimental: {},
      ga4UserId: null,
    };

    this.on(actionTypes.DATA_LOADED, this.__setGlobalData);
    this.on(actionTypes.ACTIVATE, this.__activate);
    this.on(actionTypes.RECORD_GLOBAL_DECISION, this.__recordGlobalDecision);
    this.on(actionTypes.SET_DOMCONTENTLOADED, this.__setDomContentLoaded);
    this.on(actionTypes.SET_GA4_USER_ID, this.__setGA4UserId);
  },

  /**
   * Returns the client data revision
   */
  getRevision: function() {
    return this.__state.revision;
  },

  /**
   * Returns the GlobalHoldback [0, 9999]
   * @return {number}
   */
  getGlobalHoldbackThreshold: function(): number {
    return this.__state.holdback;
  },

  /**
   * @return {string}
   */
  getProjectId: function(): string {
    return this.__state.projectId;
  },

  /**
   * @return {string|null}
   */
  getSnippetId: function(): string|null {
    return this.__state.snippetId;
  },

  /**
   * @return {string}
   */
  getAccountId: function(): string {
    return this.__state.accountId;
  },

  /**
   * @return {string}
   */
  getNamespace: function(): string {
    return this.__state.namespace;
  },

  /**
   * @return {string}
   */
  getActivationId: function(): string {
    return this.__state.activationId;
  },

  /**
   * @returns {number}
   */
  getActivationTimestamp: function(): number {
    return this.__state.activationTimestamp;
  },

  /**
   * Whether IP addresses should be anonymized
   * @returns {boolean|null}
   */
  getAnonymizeIP: function(): boolean|null {
    return this.__state.anonymizeIP;
  },

  /**
   * Whether the visitor is in the global holdback. Returns `false` if not yet decided (`null`).
   * @   returns {boolean}
   */
  isGlobalHoldback: function() {
    return !!this.__state.isGlobalHoldback;
  },

  /**
   * @typedef {Object} ListTargetingKey
   * @property {string} type
   * @property {string} name
   * @property {string} paramName
   */

  /**
   * @return {ListTargetingKey[]}
   */
  getListTargetingKeys: function(): ListTargetingKey[] {
    return this.__state.listTargetingKeys.slice();
  },

  getDCPServiceId: function() {
    return this.__state.dcpServiceId;
  },

  /**
   * @returns {DCPKeyfieldLocator[]}
   */
  getDCPKeyfieldLocators: function(): DCPKeyfieldLocator[] {
    return this.__state.dcpKeyfieldLocators;
  },

  /**
   * @returns {RecommenderService[]|null}
   */
  getRecommenderServices: function(): RecommenderService[]|null {
    return this.__state.recommenderServices;
  },

  /**
   * @returns {Function|null}
   */
  getProjectJS: function(): Function|null {
    return this.__state.projectJS;
  },

  /**
   * @returns {PluginFn[]}
   */
  getPlugins: function(): PluginFn[] {
    return this.__state.plugins;
  },

  /**
   * @returns {Object}
   */
  getExperimental: function(): any {
    return freeze.safeReference(this.__state.experimental);
  },
  /**
   * @returns {Function|null}
   */
  getGA4UserId: function(): Function|null {
    return this.__state.ga4UserId;
  },

  /**
   * @returns {boolean}
   * @see https://developer.mozilla.org/en-US/docs/Web/Events/DOMContentLoaded
   */
  domContentLoadedHasFired: function(): boolean {
    return this.__state.domContentLoaded;
  },

  /**
   * @param {Object} payload
   * @param {string} payload.activationId
   * @param {string} payload.activationTimestamp
   */
  __activate: function(payload:any) {
    this.__state.activationId = payload.activationId;
    this.__state.activationTimestamp = payload.activationTimestamp;

    // reset global holdback state on activation
    this.__state.isGlobalHoldback = null;
  },

  /**
   *
   * @param {Object} payload
   * @param {boolean} payload.isGlobalHoldback
   * @private
   */
  __recordGlobalDecision: function(payload:any) {
    var isGlobalHoldback = payload.isGlobalHoldback;
    if (this.__state.isGlobalHoldback !== null && this.__state.isGlobalHoldback !== isGlobalHoldback) {
      throw new Error('Attempted to change already set global holdback!');
    }
    this.__state.isGlobalHoldback = isGlobalHoldback;
    this.emitChange();
  },


  __setGA4UserId: function (payload: any) {
    var ga4UserId = payload.ga4UserId;

    if(!ga4UserId) {
      throw new Error('Argument "payload.ga4UserId" is missing');
    }

    this.__state.ga4UserId = ga4UserId;
    this.emitChange();
  },

  /**
   * Sets the global holdback based on the DATA object being loaded
   * @param {Object} payload
   * @param {ClientData} payload.data
   * @private
   */
  __setGlobalData: function(payload:any) {
    var updated = _.pick(payload.data, [
        'holdback',
        'accountId',
        'projectId',
        'snippetId',
        'namespace',
        'revision',
        'listTargetingKeys',
        'dcpServiceId',
        'dcpKeyfieldLocators',
        'recommenderServices',
        'anonymizeIP',
        'plugins',
        'projectJS',
        'experimental',
        'ga4UserId',
    ]);
    if (_.keys(updated).length === 0) {
      return;
    }

    var defaults = {
      listTargetingKeys: [],
      dcpServiceId: null,
      dcpKeyfieldLocators: [],
    };
    _.extend(this.__state, defaults, updated);
    this.emitChange();
  },

  __setDomContentLoaded: function() {
    this.__state.domContentLoaded = true;
    this.emitChange();
  },
};
